import styled from 'styled-components';
import React, { useState } from 'react';
import {
  Badge,
  MenuItem,
  Menu,
  Tooltip,
  Typography,
  ClickAwayListener,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { type SidebarData, type SidebarChildData } from './types';

type SidebarProps = {
  sidebarData: SidebarData[];
  defaultActivatedId?: string;
  manuallyActivatedId?: string; // for manually activation use
  onChangeActivated: (activatedId: string) => void; // for clicking or mouse move on component use
  barInnerWidth?: string;
  progressText?: string;
};

export const ArrowTopBar: React.FunctionComponent<SidebarProps> = (
  props: SidebarProps,
) => {
  const { sidebarData, defaultActivatedId, progressText, onChangeActivated } =
    props;
  const [activatedId, setActivatedId] = useState<string>(
    defaultActivatedId ?? '',
  );
  const [dropDownMeneOpen, setDropDownMeneOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentMenusList, setCurrentMenusList] = useState<SidebarChildData[]>(
    [],
  );

  const handleDisplayChildrenMenu = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseChildrenMenu = (): void => {
    setAnchorEl(null);
  };

  const handleBarBoxClick =
    (id: string, children?: SidebarChildData[]) =>
    (e: React.MouseEvent<HTMLDivElement> | React.MouseEvent): void => {
      const isActivated = checkIsActivated(id, children);
      // Display children menu if present
      if (children && children.length > 0) {
        const visibleChildren = children.filter(
          ({ visible }) => visible ?? true,
        );
        if (!visibleChildren[0]?.id) {
          return;
        }
        onChangeActivated(visibleChildren[0].id);
        setActivatedId(visibleChildren[0].id);
        handleDisplayChildrenMenu(e as React.MouseEvent<HTMLDivElement>);
        setDropDownMeneOpen(true);
        setCurrentMenusList(children);
      } else {
        // For items without children, activate if not already activated
        if (!isActivated) {
          onChangeActivated(id);
          setActivatedId(id);
        }
      }
    };

  const handleBarBoxHover =
    (children?: SidebarChildData[]) =>
    (e: React.MouseEvent<HTMLDivElement> | React.MouseEvent): void => {
      // Display children menu if present
      if (children && children.length > 0) {
        const visibleChildren = children.filter(
          ({ visible }) => visible ?? true,
        );
        if (!visibleChildren[0]?.id) {
          return;
        }
        setDropDownMeneOpen(true);
        handleDisplayChildrenMenu(e as React.MouseEvent<HTMLDivElement>);
        setCurrentMenusList(children);
      }
    };

  const handleBarBoxMouseLeave = (): void => {
    handleCloseChildrenMenu();
    setCurrentMenusList([]);
  };

  const checkIsActivated = (
    id: string,
    children?: SidebarChildData[],
  ): boolean => {
    const isSelfActivated = activatedId === id;
    let isChildActivated = false;

    if (children) {
      children.every(({ id: childId }) => {
        if (childId === activatedId) {
          isChildActivated = true;
          // stop loop if find activated child
          return false;
        }
        return true;
      });
    }

    return isSelfActivated || isChildActivated;
  };

  return (
    <ClickAwayListener onClickAway={handleBarBoxMouseLeave}>
      <Container onMouseLeave={handleBarBoxMouseLeave}>
        {sidebarData.map((sidebarTab, index) => {
          const {
            id,
            name,
            children,
            badgeInvisible = true,
            disabled = false,
          } = sidebarTab;

          const handleClick = disabled
            ? undefined
            : handleBarBoxClick(id, children);
          const isActivated = checkIsActivated(id, children);
          const isEdgeLeft = index === 0;
          const isEdgeRight = index === sidebarData.length - 1;
          const hasChildren = children && children.length > 0;

          return (
            <Tooltip
              title={disabled && progressText !== undefined ? progressText : ''}
              key={index}
            >
              <BarBox
                $activated={isActivated}
                $edgeLeft={isEdgeLeft}
                $edgeRight={isEdgeRight}
                $disabled={disabled}
                onClick={handleClick}
                onBlur={handleCloseChildrenMenu}
                onMouseEnter={
                  !disabled && hasChildren
                    ? handleBarBoxHover(children)
                    : undefined
                }
              >
                <div className="arrow w-full">
                  <Badge
                    color="warning"
                    variant="dot"
                    invisible={badgeInvisible}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Typography
                      aria-owns={
                        dropDownMeneOpen ? 'mouse-over-popover' : undefined
                      }
                      aria-haspopup="true"
                      sx={{
                        fontSize: '16px',
                      }}
                    >
                      {name}
                    </Typography>
                  </Badge>
                  {hasChildren && (
                    <MoreVert
                      sx={{
                        position: 'absolute',
                        right: '0.5rem',
                      }}
                    />
                  )}
                </div>
              </BarBox>
            </Tooltip>
          );
        })}
        <Menu
          id="mouse-over-popover"
          open={dropDownMeneOpen || Boolean(anchorEl)}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            top: '0.5rem',
            left: '-1.9rem',
            pointerEvents: 'none',
            '& .MuiPaper-root': {
              pointerEvents: 'auto',
            },
          }}
          disableScrollLock={true}
        >
          {currentMenusList?.map((child, childIndex) => {
            const {
              name: childName,
              id,
              badgeInvisible = true,
              disabled = false,
              visible = true,
            } = child;
            const isActivated = activatedId === id;
            const handleClick = disabled ? undefined : handleBarBoxClick(id);

            return (
              visible && (
                <MenuItem
                  key={childIndex}
                  onClick={handleClick}
                  selected={isActivated}
                  sx={{
                    fontSize: '14px',
                  }}
                >
                  <Badge
                    color="warning"
                    variant="dot"
                    invisible={badgeInvisible}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    {childName}
                  </Badge>
                </MenuItem>
              )
            );
          })}
        </Menu>
      </Container>
    </ClickAwayListener>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 1rem 2rem 2rem 2rem;
  top: 8rem;
  left: 0;
  display: flex;
  flex-direction: row;
`;

const BarBox = styled.div<{
  $activated?: boolean;
  $isChild?: boolean;
  $width?: string;
  $edgeLeft?: boolean;
  $edgeRight?: boolean;
  $disabled?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 2rem;
  margin: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${props => (props?.$disabled ? 'auto' : 'pointer')};

  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */

  .arrow {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-right: 3rem;
    width: 100%;
    height: 3rem;
    margin: 1.1rem;
    background-color: ${props =>
      props?.$disabled ? '#E3E3E3' : props?.$activated ? '#951d1d' : '#e2c0c0'};
    cursor: ${props => (props?.$disabled ? 'auto' : 'pointer')};
    color: ${props =>
      props?.$disabled ? '#827e7e' : props?.$activated ? '#fff' : '#951d1d'};

    font-weight: ${props => (props?.$activated ? '700' : '500')};
    transition: all 0.3s;
  }

  .arrow::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 0;

    border-color: ${props =>
      props?.$edgeLeft
        ? props.$disabled
          ? '#E3E3E3'
          : props.$activated
            ? '#951d1d'
            : '#e2c0c0'
        : props.$disabled
          ? '#E3E3E3 #E3E3E3 #E3E3E3 transparent'
          : props.$activated
            ? '#951d1d #951d1d #951d1d transparent'
            : '#e2c0c0 #e2c0c0 #e2c0c0 transparent'};

    border-width: 1.5rem;
    border-style: solid;
    transition: all 0.3s;
  }

  .arrow::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 0;
    border-color: ${props =>
      props?.$edgeRight
        ? props.$disabled
          ? '#E3E3E3'
          : props.$activated
            ? '#951d1d'
            : '#e2c0c0'
        : props.$disabled
          ? 'transparent transparent transparent #E3E3E3'
          : props.$activated
            ? 'transparent transparent transparent #951d1d'
            : 'transparent transparent transparent #e2c0c0'};

    border-width: 1.5rem;
    border-style: solid;
    transition: all 0.1s;
  }
`;
